import { useState } from 'react'

import { ButtonDefault } from '@/components/common/Button'
import { FloatingTooltip } from '@/components/common/Floating'
import { InputColorWheel } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { usePage } from '@/components/providers/PageProvider'
import { COLOR_ID_LABELS, type ColorIdType, type ColorType } from '@/defs/mtg'
import { getColorId } from '@/utils/cardUtil'
import { cn } from '@/utils/commonUtil'

import styles from './FilterInputColorIdChoices.module.scss'

type Props = {
  colorId?: ColorIdType
  colorIds: ColorIdType[]
  loading?: boolean
  onApply: (colorId?: ColorIdType) => void
  showEmpty?: boolean
}

export const FilterInputColorIdChoices = ({ colorId, colorIds, loading, onApply, showEmpty = true }: Props) => {
  const { page } = usePage()
  const [colors, setColors] = useState<ColorType[]>(colorId ? COLOR_ID_LABELS[colorId].colors : [])

  const section = page?.route?.section

  const minColorId = page?.optionsFilters?.colorIdFeatured
  const isCompact = section === 'commandersFeatured'

  const colorIdPending = colors.length > 0 ? getColorId(colors) : undefined
  const isAvailable = colorIdPending ? colorIds.includes(colorIdPending) : showEmpty
  const isChanged = colorIdPending !== colorId

  const tooltipContent =
    colorIdPending && !isAvailable ? (
      <p>That color identity ({COLOR_ID_LABELS[colorIdPending].label}) is not available.</p>
    ) : undefined

  return (
    <div className={cn(styles.container, { isCompact, styles })}>
      <InputColorWheel
        colors={colors}
        minColorId={minColorId}
        onChange={setColors}
        variant={isCompact ? 'horizontal' : undefined}
      />
      <div className={styles.button}>
        <FloatingTooltip content={tooltipContent}>
          <ButtonDefault
            aria-label='Apply Color Identity'
            disabled={!isChanged || !isAvailable}
            loading={loading}
            maxwidth
            onClick={() => onApply(colorIdPending)}
            size={isCompact ? 'xs' : 'sm'}
            type='button'
          >
            <Icon name='check' />
          </ButtonDefault>
        </FloatingTooltip>
      </div>
    </div>
  )
}
